import classNames from "classnames"

import { Color } from "../../../../constants/V2/color"
import Badge from "../../../elements/V2/Badge"
import PillButton from "../../../elements/V2/Buttons/PillButton"
import GatsbyStoryblokImage from "../../../elements/V2/GatsbyStoryblokImage"
import Typography from "../../../elements/V2/Typography"
import LayoutItem from "../../Layouts/LayoutItem"

import { getBackgroundColorClass, getBorderColorClass } from "@utils/V2/color"
import React from "react"

export type PlanConfigData = Omit<Storyblok.PlanConfig["content"], "component">

type Props = {
  planConfig: PlanConfigData
  ctaText: string
  ctaLink?: string | YocoCom.YocoLink
  ctaColor?: Color
  mobileGridOrder?: number
  key?: string
  testimonialTitle?: string
  testimonialQuote?: string
  testimonialImage?: Storyblok.Asset
  testimonialName?: string
  testimonialBusiness?: string
  tracking?: {
    event?: string
    eventKey?: string
    eventValue?: string
  }
}

const RecommendedPlanConfig = ({
  planConfig,
  ctaText,
  ctaLink,
  ctaColor = Color.Blue,
  mobileGridOrder,
  testimonialTitle,
  testimonialQuote,
  testimonialImage,
  testimonialName,
  testimonialBusiness,
  tracking,
  ...props
}: Props) => {
  const accentColor = Color.Charcoal

  const hasTestimonial = Boolean(
    testimonialTitle ||
      testimonialQuote ||
      testimonialName ||
      testimonialBusiness ||
      testimonialImage?.filename
  )

  return (
    <LayoutItem
      mobileGridOrder={mobileGridOrder}
      as="div"
      animateOnScroll
      className="col-span-full flex w-full max-w-[390px] flex-col gap-12 self-start justify-self-end sm:col-span-2 md:col-span-2 lg:col-span-4"
      key={props.key}
    >
      <div
        className={classNames(
          getBackgroundColorClass(Color.White),
          "flex flex-col gap-16 rounded-20 border p-[30px] shadow-[0px_10px_20px_0px_rgba(0,0,0,0.1)]",
          getBorderColorClass(Color.Blue)
        )}
      >
        <div className="flex items-start justify-between gap-12">
          <div className="flex items-center gap-12">
            {planConfig.icon?.filename && (
              <GatsbyStoryblokImage
                width={24}
                height={24}
                loadType="eager"
                image={planConfig.icon.filename}
                alt={planConfig.icon.alt || planConfig.title}
                className="h-24 w-24 shrink-0"
              />
            )}
            <Typography
              element="h3"
              size="lead-md-fixed"
              font="grotesk"
              weight="medium"
              text={planConfig.title}
              color={accentColor}
            />
          </div>
          {planConfig.badge?.[0]?.text && (
            <Badge
              style="solid"
              text={planConfig.badge[0].text}
              highlightedText={planConfig.badge[0].highlightedText}
              uppercaseText={false}
              textColor={planConfig.badge[0].textColor || Color.White}
              accentColor={planConfig.badge[0].color || Color.Orange}
              className="shrink-0 !px-12"
            />
          )}
        </div>

        <div className="flex flex-col items-start space-y-4">
          {planConfig.descriptionTitle && (
            <Typography
              size="body-md"
              font="grotesk"
              weight="medium"
              color={accentColor}
              text={planConfig.descriptionTitle}
            />
          )}

          <Typography
            size="body-sm-fixed"
            font="grotesk"
            weight="book"
            text={planConfig.description}
            color={accentColor}
          />
        </div>

        <div className="mt-16 flex w-full items-baseline justify-between gap-4">
          {planConfig.monthlyCost && (
            <div className="flex flex-col">
              <div className="flex items-baseline gap-4">
                <Typography
                  size="lead-lg"
                  font="grotesk"
                  weight="medium"
                  text={planConfig.monthlyCost}
                  color={accentColor}
                />
                <Typography
                  size="body-md"
                  font="grotesk"
                  weight="book"
                  text="/mo"
                  color={accentColor}
                />
              </div>
              {planConfig.priceSubscript && (
                <Typography
                  size="subscript-sm"
                  font="grotesk"
                  weight="book"
                  text={planConfig.priceSubscript}
                  color={accentColor}
                  className="opacity-60"
                />
              )}
            </div>
          )}
          {planConfig.fromAmount && (
            <div className="pb-8 text-right">
              <div className="flex items-baseline justify-end gap-2">
                <Typography
                  size="body-sm"
                  font="grotesk"
                  weight="book"
                  color={accentColor}
                  text="From"
                />
                <Typography
                  size="lead-sm"
                  font="grotesk"
                  weight="medium"
                  color={accentColor}
                  text={planConfig.fromAmount}
                  className="text-nowrap"
                />
              </div>
              <Typography
                size="subscript-sm"
                font="grotesk"
                weight="book"
                color={accentColor}
                text="In-person local debit cards"
                className="opacity-80"
              />
            </div>
          )}
        </div>

        <PillButton
          size="large"
          style="solid"
          text={ctaText}
          linkUrl={ctaLink || "/"}
          color={ctaColor}
          trackingEvent={tracking?.event}
          trackingEventKey={tracking?.eventKey}
          trackingEventValue={tracking?.eventValue}
          fontSize="body-lg-fixed"
          className="mt-16 !w-full"
        />
      </div>

      {hasTestimonial && (
        <div
          className={classNames(
            getBackgroundColorClass(Color.White),
            "flex flex-col gap-20 rounded-20 border p-[30px]",
            getBorderColorClass(Color.Gray)
          )}
        >
          <div className="flex flex-col gap-8">
            {testimonialTitle && (
              <Typography
                size="body-sm"
                font="grotesk"
                weight="book"
                text={testimonialTitle}
                color={Color.Charcoal}
                className="opacity-60"
              />
            )}
            {testimonialQuote && (
              <Typography
                size="lead-sm"
                font="grotesk"
                weight="book"
                text={`"${testimonialQuote}"`}
                color={Color.Charcoal}
              />
            )}
          </div>
          {(testimonialName ||
            testimonialBusiness ||
            testimonialImage?.filename) && (
            <div className="flex items-center gap-12">
              {testimonialImage?.filename && (
                <GatsbyStoryblokImage
                  aspectRatio="1:1"
                  width={44}
                  height={44}
                  rounded
                  image={testimonialImage.filename}
                  alt={testimonialImage.alt || testimonialName || ""}
                  className="h-[44px] w-[44px] shrink-0 rounded-full"
                />
              )}
              <div className="flex flex-col">
                {testimonialName && (
                  <Typography
                    size="body-md"
                    font="grotesk"
                    weight="medium"
                    text={testimonialName}
                    color={Color.Charcoal}
                  />
                )}
                {testimonialBusiness && (
                  <Typography
                    size="body-md"
                    font="grotesk"
                    weight="book"
                    text={testimonialBusiness}
                    color={Color.Charcoal}
                    className="opacity-50"
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </LayoutItem>
  )
}

export default RecommendedPlanConfig
